import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  computed,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  NEVER,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  tap,
  distinctUntilChanged,
  startWith,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ElementActions,
  ElementTypes,
  GaContext,
  MediaQueryService,
  OfferListFetchParams,
  isShortHaulFlight,
  FinnairThemeService,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { Cabin } from '@fcom/dapi/api/models';
import { getLocation } from '@fcom/locations';
import {
  ButtonSize,
  ButtonTheme,
  ButtonType,
  CheckBoxTheme,
  IconPosition,
  RadioItemGroupOption,
} from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { TicketSelectionPhase } from '@fcom/voluntary-change/enums';
import { AirOffersStatus, BookingAppState, SelectionPhase } from '@fcom/common/interfaces/booking';
import { BookingAirBoundsService } from '@fcom/booking/modules/ticket-selection/services';
import { loginStatus, profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { isPresent, MonitoringLogger } from '@fcom/core';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { resetAllNextFilters, setAllNextFilters } from '@fcom/common/store/actions/bounds.actions';
import { LoginStatus } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx';

import { defaultFilterValues, FlightFilters, FlightTimeOption } from '../../interfaces/flight-filters.interface';

@Component({
  selector: 'fin-flight-selection-filters',
  styleUrls: ['./flight-selection-filters.component.scss'],
  templateUrl: './flight-selection-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightSelectionFiltersComponent implements OnInit, OnChanges, OnDestroy {
  private store$ = inject<Store<BookingAppState>>(Store);
  private mediaQueryService = inject(MediaQueryService);
  private gtmService = inject(GtmService);
  private bookingAirBoundsService = inject(BookingAirBoundsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private monitoringLogger = inject(MonitoringLogger);
  private themeService = inject(FinnairThemeService);

  readonly isNewBrand = computed((): boolean => {
    return this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  });

  /**
   * Whether radio buttons in travel class filter should be disabled
   */
  @Input()
  isTravelClassChoicesDisabled: boolean;

  @Input()
  selectionPhase: SelectionPhase | TicketSelectionPhase;

  @Input()
  showRefundability = true;

  @Input()
  travelClasses$: Observable<Cabin[]>;

  /**
   * How many results the current filters produce
   * In desktop version used in all dropdowns' footer buttons
   * In mobile used in modal's footer button
   */
  @Input()
  resultsAmount: number;

  /**
   * Amount of layovers each flight has.
   * Used in stops filter
   */
  @Input()
  layoverAmounts: number[] = [];

  /**
   * Used in times filter
   */
  @Input()
  originLocationCode: string;

  /**
   * Used in times filter
   */
  @Input()
  destinationLocationCode: string;

  @Input() filters$: Observable<FlightFilters>;

  @Input()
  cabinLastRequestParam$: Observable<{ cabin: Cabin }> = of({ cabin: Cabin.MIXED });

  @Input()
  isTravelClassDisabled = false;

  @Input()
  showFlightSelectionFilters: boolean;

  @Input()
  boundsHasDirectFlights$: Observable<boolean> = NEVER;

  @Input()
  allBoundsAreDirect$: Observable<boolean> = NEVER;

  @Input()
  amountOfDirectFlights$: Observable<number> = NEVER;

  @Output()
  setFilter: EventEmitter<FlightFilters> = new EventEmitter();
  @Output()
  showSpinner: EventEmitter<void> = new EventEmitter();

  selectedTravelClass: Cabin;
  selectedDepartureTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  selectedArrivalTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  defaultInboundTravelClass: Cabin;

  departureCityName: string;
  arrivalCityName: string;

  isModalOpen: boolean;

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly CheckBoxTheme = CheckBoxTheme;
  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly FlightTimeOption = FlightTimeOption;
  readonly Cabin = Cabin;
  readonly AirOffersStatus = AirOffersStatus;
  readonly SelectionPhase = SelectionPhase;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TicketSelectionPhase = TicketSelectionPhase;

  nextDepartureTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  nextArrivalTimesForm: FormGroup<Record<string, FormControl<boolean>>>;
  directFlightsForm = new FormGroup<{ shortcut: FormControl<boolean> }>({
    shortcut: new FormControl<boolean>(false),
  });

  awardQueryParams$: Observable<OfferListFetchParams>;
  isMobile$: Observable<boolean>;
  shouldNotClearTravelClass: boolean;
  defaultTravelClass: Cabin;
  selectedFiltersLength$: BehaviorSubject<number> = new BehaviorSubject(0);
  subscriptions: Subscription = new Subscription();
  filtersForm: FormGroup<{
    stopsCount: FormControl<null | string>;
    refundable: FormControl<string>;
    cabin: FormControl<Cabin>;
    directFlight: FormControl<boolean>;
    departureMORNING: FormControl<boolean>;
    departureAFTERNOON: FormControl<boolean>;
    departureEVENING: FormControl<boolean>;
    arrivalMORNING: FormControl<boolean>;
    arrivalAFTERNOON: FormControl<boolean>;
    arrivalEVENING: FormControl<boolean>;
  }>;
  travelClassFilters: RadioItemGroupOption[] = [];
  updateFilters$: Subject<void> = new Subject();
  loginStatus$: Observable<LoginStatus>;

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());

    this.subscriptions.add(
      this.cabinLastRequestParam$
        ?.pipe(
          filter(Boolean),
          withLatestFrom(this.store$.pipe(profileOrUndefinedIfNotLoggedIn()), this.isLongHaul()),
          // Don't do initial filtering for corporate long-hauls
          filter(([_, profile, isLongHaul]) => !(profile?.isCorporate && isLongHaul)),
          take(1),
          tap(([params]) => {
            if (this.selectionPhase === SelectionPhase.OUTBOUND) {
              this.setFilter.emit({ cabin: params.cabin });
            }
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.travelClasses$
        .pipe(
          tap((travelClasses) => {
            const defaultIsNotMixedCabinForInbound =
              this.selectionPhase === SelectionPhase.INBOUND && this.defaultInboundTravelClass !== Cabin.MIXED;
            this.shouldNotClearTravelClass =
              this.isTravelClassDisabled || defaultIsNotMixedCabinForInbound || travelClasses.length === 1;
          }),
          take(1)
        )
        .subscribe()
    );
    this.subscriptions.add(
      combineLatest([this.travelClasses$, this.cabinLastRequestParam$])
        .pipe(take(1))
        .subscribe(([travelClasses, params]) => {
          this.defaultTravelClass = travelClasses.includes(Cabin.MIXED) ? Cabin.MIXED : params.cabin;
        })
    );

    this.subscriptions.add(
      this.travelClasses$.pipe(take(1)).subscribe((cabinArray) => {
        cabinArray.forEach((cabin) => {
          this.travelClassFilters.push({
            value: cabin,
            label: 'travelClass.' + cabin.toLowerCase(),
          });
        });
      })
    );

    this.subscriptions.add(
      this.filters$.subscribe((filters) => {
        this.handleFilterChange(filters);
      })
    );

    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.updateLocations();

    this.filtersForm = new FormGroup({
      stopsCount: new FormControl<null | string>(null),
      cabin: new FormControl<Cabin>({ value: this.defaultTravelClass, disabled: this.isTravelClassChoicesDisabled }),
      refundable: new FormControl<string>('false'),
      directFlight: new FormControl<boolean>(false),
      departureMORNING: new FormControl<boolean>(true),
      departureAFTERNOON: new FormControl<boolean>(true),
      departureEVENING: new FormControl<boolean>(true),
      arrivalMORNING: new FormControl<boolean>(true),
      arrivalAFTERNOON: new FormControl<boolean>(true),
      arrivalEVENING: new FormControl<boolean>(true),
    });

    this.subscriptions.add(
      combineLatest([
        this.filtersForm.get('stopsCount').valueChanges.pipe(startWith(this.filtersForm.get('stopsCount').value)),
        this.allBoundsAreDirect$.pipe(startWith(false)),
      ]).subscribe(([stopsCount, allDirect]) => {
        this.filtersForm.get('directFlight').setValue(stopsCount === '0' || allDirect, { emitEvent: false });
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.boundsHasDirectFlights$.pipe(startWith(true)),
        this.allBoundsAreDirect$.pipe(startWith(false)),
      ]).subscribe(([hasDirectFlights, allDirect]) => {
        if (!hasDirectFlights || allDirect) {
          this.filtersForm.get('directFlight').disable({ emitEvent: false });
        } else {
          this.filtersForm.get('directFlight').enable({ emitEvent: false });
        }
      })
    );

    this.subscriptions.add(
      this.filtersForm.get('directFlight').valueChanges.subscribe((checked) => {
        this.onToggleDirectFlights(checked);
      })
    );

    this.subscribeToTimeGaTracking('departure');
    this.subscribeToTimeGaTracking('arrival');

    this.subscriptions.add(
      this.filters$
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          withLatestFrom(this.allBoundsAreDirect$)
        )
        .subscribe(([filters, allBoundsAreDirect]) => {
          this.onUpdateFilters(filters, allBoundsAreDirect);
        })
    );

    this.subscriptions.add(
      this.updateFilters$
        .pipe(withLatestFrom(this.filters$, this.allBoundsAreDirect$))
        .subscribe(([_, filters, allBoundsAreDirect]) => {
          this.onUpdateFilters(filters, allBoundsAreDirect);
        })
    );

    this.updateNextFiltersFromFormChanges();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  ngOnChanges(): void {
    this.updateLocations();
  }

  onUpdateFilters(filters: FlightFilters, allBoundsAreDirect: boolean): void {
    const refundable = filters.refundable === true ? 'true' : 'false';
    const maxStops = filters.maxStops == null ? null : String(filters.maxStops);
    const stopsCount = allBoundsAreDirect ? '0' : maxStops;

    this.filtersForm.patchValue({ stopsCount, cabin: filters.cabin, refundable }, { emitEvent: false });
    for (const key of Object.keys(FlightTimeOption)) {
      const v = FlightTimeOption[key as keyof typeof FlightTimeOption];
      this.patchTimeControl('departure', key, filters.departureTime?.includes(v) ?? true);
      this.patchTimeControl('arrival', key, filters.arrivalTime?.includes(v) ?? true);
    }

    this.setNextFilters({ stopsCount, cabin: filters.cabin, refundable });
  }

  handleFilterChange(filters: FlightFilters): void {
    if (
      this.selectionPhase === SelectionPhase.OUTBOUND &&
      this.selectedTravelClass &&
      filters.cabin !== this.selectedTravelClass
    ) {
      this.subscriptions.add(
        this.activatedRoute.queryParams.pipe(take(1)).subscribe((p) => {
          try {
            const changedJson = JSON.stringify({
              ...JSON.parse(p.json),
              cabin: filters.cabin,
            });
            const navigationExtras: NavigationExtras = {
              queryParams: {
                json: changedJson,
              },
              queryParamsHandling: 'merge',
            };
            this.router.navigate([], navigationExtras);
          } catch (error) {
            this.monitoringLogger.warn('Failed to parse deeplink', { json: p.json, error });
          }
        })
      );
      this.refetchBounds(filters.cabin);
    }

    this.selectedTravelClass = filters.cabin;
    this.selectedDepartureTimes = filters.departureTime;
    this.selectedArrivalTimes = filters.arrivalTime;

    if (this.selectionPhase === SelectionPhase.INBOUND && !this.defaultInboundTravelClass) {
      this.defaultInboundTravelClass = filters.cabin;
    }

    this.updateSelectedFiltersLength(filters);
  }

  updateNextFiltersFromFormChanges(): void {
    this.subscriptions.add(
      this.filtersForm.valueChanges
        .pipe(
          startWith(this.filtersForm.getRawValue()),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
        .subscribe((next) => {
          this.setNextFilters(next);
        })
    );
  }

  setNextFilters(formValues: Partial<{ stopsCount: string; refundable: string; cabin: Cabin }>): void {
    this.store$.dispatch(
      setAllNextFilters({
        nextFilters: {
          maxStops: formValues.stopsCount == null ? null : Number(formValues.stopsCount),
          cabin: formValues.cabin,
          refundable: formValues.refundable === 'true',
          departureTime: this.timesFromForm('departure'),
          arrivalTime: this.timesFromForm('arrival'),
        },
      })
    );
  }

  sendFiltersGaEvent(elementName: string): void {
    this.gtmService.trackElement(
      elementName,
      GaContext.BOOKING_FLOW,
      ElementTypes.RADIO,
      undefined,
      ElementActions.CLICK
    );
  }

  updateSelectedFiltersLength(filters: FlightFilters): void {
    this.subscriptions.add(
      this.allBoundsAreDirect$.pipe(take(1)).subscribe((allBoundsAreDirect) => {
        const count = [
          filters.maxStops !== defaultFilterValues.defaultStopsOption && !allBoundsAreDirect,
          filters.refundable !== defaultFilterValues.defaultRefundabilityOption && filters.refundable !== undefined,
          filters.cabin !== defaultFilterValues.defaultTravelClass && filters.cabin !== undefined,
          (filters.departureTime?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
          (filters.arrivalTime?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
        ].filter(Boolean).length;

        this.selectedFiltersLength$.next(count);
      })
    );
  }

  private isLongHaul(): Observable<boolean> {
    return combineLatest([
      this.store$.select(getLocation(this.originLocationCode)),
      this.store$.select(getLocation(this.destinationLocationCode)),
    ]).pipe(
      take(1),
      map(([origin, destination]) => !isShortHaulFlight(origin, destination))
    );
  }

  private refetchBounds(cabin: Cabin) {
    const queryParams = this.bookingAirBoundsService.getLastRequestParams();

    if (this.selectionPhase === SelectionPhase.OUTBOUND && !queryParams?.isAward) {
      this.subscriptions.add(
        this.loginStatus$.pipe(take(1)).subscribe((status) => {
          this.bookingAirBoundsService.triggerFetchBounds({ ...queryParams, cabin }, status);
        })
      );
    }
  }

  clearAllFilters(contextForAnalytics: 'desktop' | 'mobile' | 'empty-state' | 'master-reset'): void {
    this.showSpinner.emit();

    this.store$.dispatch(resetAllNextFilters());

    this.subscriptions.add(
      this.allBoundsAreDirect$
        .pipe(
          take(1),
          filter((value) => value === false)
        )
        .subscribe(() => {
          this.filtersForm.get('stopsCount').setValue(null);
        })
    );
    this.filtersForm.get('cabin').setValue(this.defaultTravelClass, { emitEvent: false });

    for (const key of Object.keys(FlightTimeOption)) {
      this.patchTimeControl('departure', key, true);
      this.patchTimeControl('arrival', key, true);
    }

    if (contextForAnalytics === 'master-reset' || contextForAnalytics === 'empty-state') {
      this.setFilter.emit({
        maxStops: defaultFilterValues.defaultStopsOption,
        cabin: this.defaultTravelClass,
        refundable: defaultFilterValues.defaultRefundabilityOption,
        departureTime: [...defaultFilterValues.defaultTimeOptions],
        arrivalTime: [...defaultFilterValues.defaultTimeOptions],
      });
    }

    this.gtmService.trackElement(
      `booking-flow-clear-all-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  updateLocations(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.select(getLocation(this.originLocationCode)),
        this.store$.select(getLocation(this.destinationLocationCode)),
      ])
        .pipe(take(1))
        .subscribe(([origin, destination]) => {
          this.departureCityName = origin?.cityName;
          this.arrivalCityName = destination?.cityName;
        })
    );
  }

  keepOrder(_a: unknown, _b: unknown): number {
    return 0;
  }

  getTimeRange(option: FlightTimeOption): string {
    switch (option) {
      case FlightTimeOption.MORNING:
        return '00:00 - 11:59 h';
      case FlightTimeOption.AFTERNOON:
        return '12:00 - 16:59 h';
      case FlightTimeOption.EVENING:
        return '17:00 - 23:59 h';
      default:
        return '';
    }
  }

  showModal(): void {
    this.isModalOpen = true;

    this.sendAnalytics('modal');
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.updateFilters$.next();
  }

  updateNextFiltersToStore(): void {
    this.showSpinner.emit();

    const filters = this.filtersForm.getRawValue();
    const nextFilters: FlightFilters = {
      departureTime: this.timesFromForm('departure'),
      arrivalTime: this.timesFromForm('arrival'),
      maxStops: isPresent(filters.stopsCount) ? Number(filters.stopsCount) : null,
      cabin: filters.cabin,
      refundable: filters.refundable === 'true',
    };

    this.setFilter.emit(nextFilters);

    this.isModalOpen = false;
  }

  sendAnalytics(contextForAnalytics: string): void {
    this.gtmService.trackElement(
      `booking-flow-open-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  onToggleDirectFlights(checked: boolean): void {
    this.showSpinner.emit();

    this.filtersForm.get('stopsCount').setValue(checked ? '0' : null);
    this.setFilter.emit({ maxStops: checked ? 0 : null });

    this.gtmService.trackElement(
      'booking-flow-direct-flights-shortcut-checkbox',
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      checked ? 'checked' : 'unchecked',
      ElementActions.CLICK
    );
  }

  private timesFromForm(prefix: 'departure' | 'arrival'): FlightTimeOption[] {
    const raw = this.filtersForm.getRawValue() as Record<string, unknown>;
    return Object.values(FlightTimeOption).filter((v) => raw[prefix + v]);
  }

  private patchTimeControl(prefix: 'departure' | 'arrival', key: string, value: boolean): void {
    (this.filtersForm.get(prefix + key) as FormControl<boolean>)?.setValue(value, { emitEvent: false });
  }

  private subscribeToTimeGaTracking(prefix: 'departure' | 'arrival'): void {
    for (const key of Object.keys(FlightTimeOption)) {
      const option = FlightTimeOption[key as keyof typeof FlightTimeOption];
      this.subscriptions.add(
        this.filtersForm.get(`${prefix}${key}`).valueChanges.subscribe(() => {
          this.gtmService.trackElement(
            `booking-flow-select-filter-${prefix}-${option.toLowerCase()}`,
            GaContext.BOOKING_FLOW,
            ElementTypes.CHECKBOX,
            undefined,
            ElementActions.CLICK
          );
        })
      );
    }
  }
}
